import type {
  DocumentReview,
  ReviewAction,
  ReviewActivity,
  ReviewActorType,
} from '../types/review';
import type { AuditEvent } from '../types/signing';
import { auditActionLabel } from './auditActions';

type TFn = (key: string, fallback: string) => string;

/**
 * Reading a review's history: what each entry IS, what it SAYS, and whether it is still
 * waiting on the person looking at it.
 *
 * Shared by the activity panel and the chat sidebar so there is one answer per question.
 * The two used to disagree — the chat knew three actions and the admin list knew seven, four
 * of which the backend has never emitted — and the way that showed up was SCREAMING_SNAKE on
 * screen.
 */

/* ── What the entry is ──────────────────────────────────────────────────────── */

/**
 * The six kinds the design distinguishes with a pill — the prototype's `TYPE_META`
 * (`ReviewerDocumentPage.jsx:41-48`).
 *
 * It was three ('change' | 'comment' | 'review') and everything that was not an edit or a
 * remark fell into 'review', so starting a review, answering one, approving a clause and
 * closing the round all read as the same event. The split is not decoration: the pill is the
 * only thing on an entry that says what KIND of thing happened before you read the sentence.
 *
 * Every value maps onto contract-service's existing `ActivityAction` enum — this needed no
 * backend change, only a reading of what the enum already emits.
 */
export type ActivityKind =
  | 'change'
  | 'comment'
  | 'response'
  | 'review'
  | 'workflow'
  | 'approval';

/** Someone altered the document or the data merged into it. */
const CHANGE_ACTIONS = new Set([
  'CHANGES_SUBMITTED',
  'CLIENT_REQUESTED_CHANGES',
  'DOCUMENT_REPLACED',
  'AUTHOR_FIELDS_UPDATED',
  // Tracked changes read back out of a DOCX (WP-14974).
  'DOCUMENT_SUGGESTION',
]);
/** Someone wrote a remark. */
const COMMENT_ACTIONS = new Set([
  'NOTE_ADDED',
  // A comment written inside a DOCX (WP-14974). Same pill as a note typed in the panel —
  // to the person reading the rail they are the same kind of thing.
  'DOCUMENT_COMMENT',
]);
/** The other side answered something that was asked. */
const RESPONSE_ACTIONS = new Set([
  'CHANGES_RESPONDED',
  // WP-14972 — Workpay went through what was raised. An answer, not a new request.
  'CHANGES_DECIDED',
]);
/**
 * Something was accepted and closed off.
 *
 * `ANNOTATION_RESOLVED` belongs here rather than with the comments: resolving one is the
 * author agreeing it is dealt with, which is the prototype's "Approved the revised salary",
 * not a second remark.
 */
const APPROVAL_ACTIONS = new Set(['AUTHOR_AUTO_APPROVED', 'ANNOTATION_RESOLVED']);
/** A decision about the review itself. */
const REVIEW_ACTIONS = new Set([
  'MARKED_COMPLETE',
  'REVIEW_FORCE_COMPLETED',
  'REVIEW_CANCELLED',
]);

/**
 * Anything else is 'workflow' — the prototype's own fallback
 * (`TYPE_META[e.type] ?? TYPE_META.workflow`). That covers `REVIEW_STARTED` and
 * `TOKEN_REGENERATED` today, and degrades an action added backend-first to the neutral pill
 * rather than mislabelling it as a review decision.
 */
export function activityKind(action: string): ActivityKind {
  if (CHANGE_ACTIONS.has(action)) return 'change';
  if (COMMENT_ACTIONS.has(action)) return 'comment';
  if (RESPONSE_ACTIONS.has(action)) return 'response';
  if (APPROVAL_ACTIONS.has(action)) return 'approval';
  if (REVIEW_ACTIONS.has(action)) return 'review';
  return 'workflow';
}

/**
 * Plumbing. Recorded, audited, and no business being in a rail whose job is "what was asked
 * for, how it was settled, and what people said about it" (2026-11-02, demo feedback: "a lot
 * of garbage or unuseful info is getting recorded").
 *
 * Reissuing a review link is a fact about a TOKEN, not about the contract. It is in the audit
 * trail, which is the surface for who-touched-the-record; here it occupied a full entry
 * between two clause rewrites. Hidden rather than demoted, because there is no reading of it
 * that answers a question a reviewer has.
 */
const HIDDEN_ACTIONS = new Set(['TOKEN_REGENERATED']);

/*
 * `CHANGES_SUBMITTED` is deliberately NOT hidden, though it looks like a duplicate of the
 * per-edit rows beneath it. It is not written by the same path: the submit writes it
 * synchronously (`ReviewServiceImpl.doSubmitReviewerChanges`), while `DOCUMENT_SUGGESTION`
 * rows come from an event listener on the OnlyOffice SAVE callback. Three real cases produce
 * no suggestion rows at all, and the submit row is then the only record that the reviewer
 * submitted anything:
 *   - a PDF review — the recorder returns early on a non-DOCX and the reviewer marks up with
 *     annotations instead;
 *   - a reviewer who read the contract and submitted without editing it;
 *   - a submit that CLOSES the review — the recorder refuses to write once the status leaves
 *     IN_PROGRESS, so edits not yet auto-saved leave nothing behind.
 * It renders as a quiet node on the rail, which is the right weight for it.
 */

/**
 * Signing-audit actions that never reach the trail.
 *
 * Two different reasons, both "nobody did anything a reader needs to know about":
 *
 *  - **Nothing was decided.** Creating a request and opening one are not actions on the
 *    contract — the request existing is why the screen is open, and `ENVELOPE_VIEWED` fires
 *    every time anyone looks. `FIELD_FILLED` fires once PER FIELD, so a ten-field contract
 *    buried the trail by itself. (User, 2026-11-02: "only when some action is taken then add
 *    in log — like not just opening for the review, when I actually send something for review
 *    then capture; created and useless kind of logs should not be there".)
 *  - **It already appears, with its actor.** The review table records the review's own
 *    lifecycle, and it is the only record on a TEMPLATE review, which has no envelope and so
 *    no audit stream at all. Keeping both printed every review milestone twice — "The review
 *    finished" from one and again from the other.
 *
 * The success halves of the employee handoff are plumbing between services. Their FAILED
 * twins are not dropped: somebody has to act on those.
 */
const HIDDEN_AUDIT_ACTIONS = new Set([
  'ENVELOPE_CREATED',
  'ENVELOPE_VIEWED',
  'FIELD_FILLED',
  'EMPLOYEE_HANDOFF_PUSHED',
  'EMPLOYEE_PROFILE_WRITEBACK_PUSHED',
  'ENVELOPE_SENT_FOR_REVIEW',
  'ENVELOPE_RESENT_FOR_REVIEW',
  'ENVELOPE_REVIEW_COMPLETED',
  'ENVELOPE_REVIEW_CANCELLED',
]);

/**
 * Does this entry carry WORDS someone wrote or changed?
 *
 * The rail's content is the reviewing itself: a tracked change (which carries the clause and
 * the wording), a comment, a note. Everything else — started, submitted, decided, completed,
 * cancelled — is punctuation: true, worth showing, and worth one dated line rather than a
 * card the same size as a rewritten clause.
 *
 * Before this, every action got the same card, so "Reissued the review link" and "Replaced
 * wording in Clause 7" were equally loud and a reader scanning for the second had to read
 * past the first. The demo put it plainly: the only thing of value is the text that changed
 * and who changed it.
 */
const CONTENT_ACTIONS = new Set([
  // A tracked change read out of the DOCX: carries the clause and the wording.
  'DOCUMENT_SUGGESTION',
  // A comment inside the DOCX: carries the remark and the passage.
  'DOCUMENT_COMMENT',
  // A note typed in this panel, and every annotation sentinel, which all ride on NOTE_ADDED.
  'NOTE_ADDED',
  // Not new words, but it is the "how it was settled" half of a comment thread, which is
  // exactly what the rail is for.
  //
  // ⚠ Dead today: every resolve is recorded as NOTE_ADDED with an `annotation_resolved:`
  // sentinel (`ReviewAnnotationServiceImpl.recordAuthorActivity` hardcodes the action), so the
  // enum value is never written. Kept because the sentinel path lands on NOTE_ADDED above and
  // the day the backend writes the real action it should behave the same.
  'ANNOTATION_RESOLVED',
  /**
   * "Asked for changes" — the client's Request changes. It carries no message, so the shape
   * rule would leave it a quiet node, and it is the single most actionable thing an author
   * can be sent. Named here so the rail does not render the one entry that needs an answer
   * smaller and greyer than a one-word tracked change.
   */
  'CLIENT_REQUESTED_CHANGES',
]);

export function isContentEntry(entry: ActivityEntry): boolean {
  if (entry.headline) return false; // a merged signing-audit event — a workflow fact
  if (CONTENT_ACTIONS.has(entry.activity.action)) return true;
  /**
   * A milestone someone WROTE ON is not punctuation.
   *
   * Deciding on the action alone threw the words away: the milestone line renders a headline
   * and a date and never reads `message`, and the backend writes real prose onto several of
   * the demoted actions — `CHANGES_DECIDED` carries "I accepted at least one change" or "I
   * rejected every change", `MARKED_COMPLETE` names the reviewers who were closed out,
   * `DOCUMENT_REPLACED` names the revision, `CHANGES_RESPONDED` carries the author's typed
   * reply. On `CHANGES_DECIDED` that was the whole outcome of a round of review, and after
   * the demotion it appeared nowhere at all: accepted and rejected rendered the same line.
   *
   * A sentinel is not prose — it is an id or a wire token the panel translates — so a row
   * whose entire message is one is still a milestone. The `(_k, f) => f` translator asks only
   * "is this a sentinel?", never for a user-visible string.
   */
  if (ALWAYS_MILESTONE.has(entry.activity.action)) return false;
  if (entry.activity.quotedText?.trim()) return true;
  const message = entry.activity.message?.trim();
  return !!message && !sentinelLabel(message, (_k, fallback) => fallback);
}

/**
 * Milestones that carry a message but are still punctuation.
 *
 * `REVIEW_STARTED` ALWAYS has one — the author's covering note if they wrote one, and the
 * literal "Sent document for review" if they did not (`ReviewServiceImpl.java:566-567`), or
 * "Request changes" when the client opened the round. So the shape rule above would promote
 * the opening of every single review to a card, which is the noise this demotion exists to
 * remove. The cost is a covering note that is not shown; the alternative is a card on every
 * review saying the review started, which the line already says.
 */
const ALWAYS_MILESTONE = new Set([
  'REVIEW_STARTED',
  /**
   * Both carry a server-written sentence, and both are punctuation by the same argument the
   * demotion rests on. `AUTHOR_AUTO_APPROVED` is the clearest case there is: its message says
   * that nobody wrote anything ("Auto-approved — no changes suggested by reviewers"), so the
   * shape rule was giving a card the size of a rewritten clause to the statement that there
   * was nothing to read. `REVIEW_CANCELLED` ("Superseded by a new review") is the same.
   * (Re-review, 2026-11-02.)
   */
  'AUTHOR_AUTO_APPROVED',
  'REVIEW_CANCELLED',
  /**
   * Both carry a message only SOMETIMES — `MARKED_COMPLETE` when the author closed out
   * stragglers and left a note, `REVIEW_FORCE_COMPLETED` never today but nothing stops it.
   * Without naming them, the same event renders as a card on one review and a quiet node on
   * the next, in the same column, decided by whether the server wrote a sentence. A close
   * note is server punctuation, not somebody's words.
   */
  'MARKED_COMPLETE',
  'REVIEW_FORCE_COMPLETED',
]);

export function activityKindLabel(kind: ActivityKind, t: TFn): string {
  switch (kind) {
    case 'change':
      return t('K_REVIEW_KIND_CHANGE', 'Document change');
    case 'comment':
      return t('K_REVIEW_KIND_COMMENT', 'Comment');
    case 'response':
      return t('K_REVIEW_KIND_RESPONSE', 'Response');
    case 'approval':
      return t('K_REVIEW_KIND_APPROVAL', 'Approval');
    case 'review':
      return t('K_REVIEW_KIND_REVIEW', 'Review action');
    default:
      return t('K_REVIEW_KIND_WORKFLOW', 'Workflow');
  }
}

/* ── What the entry says ────────────────────────────────────────────────────── */

/**
 * The annotation endpoints store a SENTINEL rather than a human message — `"<verb>:" + <uuid>`
 * (`ReviewAnnotationServiceImpl.java` — one per annotation create / update / resolve / delete
 * and comment add / delete, from reviewers and, since 2026-10-25, the author side too) — so the SSE broadcast can say
 * which row changed. It was never meant to be read, and it reached the screen: a reviewer
 * highlighted a passage, typed a comment, and the panel answered `annotation_created:a3f91e…`.
 *
 * ⚠ **These prefixes are a WIRE CONTRACT now, not a display detail.** Since BE V33 the server
 * resolves a reply's parent by matching `annotation_created:<annotationId>` against
 * `review_activity.message` (`ReviewActivityRepository.findFirstByReviewIdAndMessage`). Rewrite
 * the stored message to something human and threading stops working — silently, with no type
 * error and nothing on this side to notice.
 */
const ANNOTATION_SENTINELS: Array<[string, string, string]> = [
  ['annotation_created:', 'K_REVIEW_EVENT_ANNOTATION_ADDED', 'Commented on a passage'],
  ['annotation_updated:', 'K_REVIEW_EVENT_ANNOTATION_UPDATED', 'Edited a comment'],
  ['annotation_deleted:', 'K_REVIEW_EVENT_ANNOTATION_DELETED', 'Removed a comment'],
  ['annotation_resolved:', 'K_REVIEW_EVENT_ANNOTATION_RESOLVED', 'Resolved a comment'],
  ['comment_added:', 'K_REVIEW_EVENT_COMMENT_ADDED', 'Replied to a comment'],
  ['comment_deleted:', 'K_REVIEW_EVENT_COMMENT_DELETED', 'Removed a reply'],
];

/**
 * A tracked change read out of a DOCX, one entry per edit (WP-14961, 2026-10-29).
 *
 * The payload after the colon is `<clause>\u001f<new wording>` — the passage the edit sits in,
 * then what was typed. The old wording, where there is any, arrives on the activity's own
 * `quotedText`; `docx_removed:` carries no new wording, because the struck-out words ARE the
 * quote. Keep in step with the sentinels in `ReviewDocxActivityRecorder`.
 *
 * A row written before the clause was recorded carries no separator, and all of its payload is
 * wording — which is why `splitDocxPayload` keys on the separator's presence rather than on a
 * field count.
 */
const DOCX_EDIT_SENTINELS: Array<[string, string, string]> = [
  ['docx_added:', 'K_REVIEW_EVENT_DOCX_ADDED', 'Added wording'],
  ['docx_removed:', 'K_REVIEW_EVENT_DOCX_REMOVED', 'Removed wording'],
  ['docx_replaced:', 'K_REVIEW_EVENT_DOCX_REPLACED', 'Replaced wording'],
];

/**
 * Did this tracked change REPLACE wording? Then the entry holds both halves of a transition —
 * the old words on `quotedText`, the new ones in the sentinel — and the panel renders them as
 * one before-and-after rather than as a quotation with a remark under it.
 *
 * Keyed on the prefix, not on "has a quote and a note": a comment anchored to a passage has
 * both of those too, and showing it as a rewrite would claim the contract had changed when
 * somebody only asked a question about it.
 */
export function isWordingReplacement(message: string | undefined): boolean {
  return !!message?.startsWith('docx_replaced:');
}

/**
 * The rolled-up DOCX suggestion entry: contract-service used to write `docx_suggestions:<n>`
 * rather than a sentence, so the count could be rendered in the reader's own language.
 *
 * ⚠ No longer WRITTEN — the recorder emits one entry per edit now, carrying the words, because
 * a count told the reader only that something had changed and left them to find it in the
 * document. Still READ: rows recorded before that change are in the trail for good, and
 * dropping this would put `docx_suggestions:5` on screen.
 */
const SUGGESTION_SENTINEL = 'docx_suggestions:';

function suggestionLabel(message: string, t: TFn): string | undefined {
  const n = Number(message.slice(SUGGESTION_SENTINEL.length));
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n === 1
    ? t('K_REVIEW_EVENT_DOCX_SUGGESTION_ONE', 'Suggested 1 change')
    : t('K_REVIEW_EVENT_DOCX_SUGGESTIONS', 'Suggested {n} changes').replace('{n}', String(n));
}

/**
 * The id a sentinel carries, so the text it stands for can be fetched and put back.
 *
 * `annotation_created:` / `annotation_updated:` / `annotation_resolved:` / `annotation_deleted:`
 * name a `review_annotation`; the two `comment_` prefixes name a `review_annotation_comment`.
 * Returns the id only for the prefixes whose row actually HOLDS a message — a delete has
 * nothing left to read.
 */
export function annotationIdFromMessage(message: string | undefined): string | undefined {
  if (!message) return undefined;
  for (const prefix of ['annotation_created:', 'annotation_updated:', 'annotation_resolved:']) {
    if (message.startsWith(prefix)) return message.slice(prefix.length) || undefined;
  }
  return undefined;
}

/** As {@link annotationIdFromMessage}, for a reply's own row. */
export function commentIdFromMessage(message: string | undefined): string | undefined {
  if (!message) return undefined;
  return message.startsWith('comment_added:')
    ? message.slice('comment_added:'.length) || undefined
    : undefined;
}

/** Does this review's history contain a reply at all? Decides whether to go and fetch any. */
export function hasReplySentinel(messages: Array<string | undefined>): boolean {
  return messages.some((m) => !!commentIdFromMessage(m));
}

/**
 * The stand-in for a sentinel message, or undefined when a human wrote it.
 *
 * Worth saying why a caller would want this separately from {@link readableMessage}: all five
 * annotation paths record the SAME action, `NOTE_ADDED`, and the actual verb lives only in the
 * sentinel. So on four of the five `activityActionLabel` returns "Added a note" for something
 * that was not an addition — a deletion rendered as "Added a note" above "Removed a comment",
 * the headline contradicting the line under it. Where this returns a value it IS the headline,
 * being strictly more specific than the action it rides on.
 */
/** Both families in one list, built once — this runs per entry per render. */
const ALL_SENTINELS: Array<[string, string, string]> = [
  ...DOCX_EDIT_SENTINELS,
  ...ANNOTATION_SENTINELS,
];

export function sentinelLabel(message: string | undefined, t: TFn): string | undefined {
  if (!message) return undefined;
  if (message.startsWith(SUGGESTION_SENTINEL)) return suggestionLabel(message, t);
  for (const [prefix, key, fallback] of ALL_SENTINELS) {
    if (message.startsWith(prefix)) return t(key, fallback);
  }
  return undefined;
}

/** Between the clause and the wording — the recorder's `CLAUSE_SEP`. */
const CLAUSE_SEP = '\u001f';

/**
 * A DOCX-edit sentinel's payload, split into the clause it sits in and the wording.
 *
 * No separator means a row recorded before clauses were, so all of it is wording — never a
 * clause, which would put a fragment of the contract in the place a heading belongs.
 */
function splitDocxPayload(message: string): { clause?: string; text?: string } {
  for (const [prefix] of DOCX_EDIT_SENTINELS) {
    if (!message.startsWith(prefix)) continue;
    const payload = message.slice(prefix.length);
    const sep = payload.indexOf(CLAUSE_SEP);
    if (sep < 0) return { text: payload.trim() || undefined };
    return {
      clause: payload.slice(0, sep).trim() || undefined,
      text: payload.slice(sep + 1).trim() || undefined,
    };
  }
  return {};
}

/**
 * The words a DOCX-edit sentinel carries, or undefined when it carries none.
 *
 * These are the only sentinels whose payload is TEXT rather than an id, so the panel can read
 * what was typed straight off the row instead of fetching anything. An annotation sentinel
 * names a row to go and read; this one IS the reading.
 */
export function sentinelPayload(message: string | undefined): string | undefined {
  if (!message) return undefined;
  return splitDocxPayload(message).text;
}

/**
 * Where in the contract a DOCX edit was made — the clause the recorder found above it.
 *
 * This is the DOCX answer to the question a page number answers for a PDF annotation. Word's
 * XML carries no pagination, so the backend names the nearest heading instead; undefined when
 * it found none, or for a row recorded before it looked.
 */
export function sentinelClause(message: string | undefined): string | undefined {
  if (!message) return undefined;
  return splitDocxPayload(message).clause;
}

/** The stored message when a human wrote it, otherwise a readable stand-in. Never a sentinel. */
export function readableMessage(message: string | undefined, t: TFn): string | undefined {
  if (!message) return undefined;
  return sentinelLabel(message, t) ?? message;
}

/**
 * Every value contract-service's `ActivityAction` enum can emit. Keep it in step with the enum
 * rather than leaning on the fallback, which exists only so an action added backend-first
 * degrades to something readable instead of shouting its wire name.
 */
export function activityActionLabel(action: string, t: TFn): string {
  switch (action) {
    case 'REVIEW_STARTED':
      return t('K_REVIEW_ACTION_REVIEW_STARTED', 'Started the review');
    case 'CHANGES_SUBMITTED':
      return t('K_REVIEW_ACTION_CHANGES_SUBMITTED', 'Submitted changes');
    case 'CHANGES_RESPONDED':
      return t('K_REVIEW_ACTION_RESPONDED', 'Responded');
    case 'CHANGES_DECIDED':
      return t('K_REVIEW_ACTION_CHANGES_DECIDED', 'Went through the changes');
    case 'CLIENT_REQUESTED_CHANGES':
      return t('K_REVIEW_ACTION_CLIENT_REQUESTED_CHANGES', 'Asked for changes');
    case 'NOTE_ADDED':
      return t('K_REVIEW_ACTION_NOTE_ADDED', 'Added a note');
    case 'MARKED_COMPLETE':
      return t('K_REVIEW_ACTION_MARKED_COMPLETE', 'Marked the review complete');
    case 'REVIEW_FORCE_COMPLETED':
      return t('K_REVIEW_ACTION_FORCE_COMPLETED', 'Closed the review');
    case 'REVIEW_CANCELLED':
      return t('K_REVIEW_ACTION_REVIEW_CANCELLED', 'Cancelled the review');
    case 'DOCUMENT_REPLACED':
      return t('K_REVIEW_ACTION_DOCUMENT_REPLACED', 'Replaced the document');
    case 'AUTHOR_FIELDS_UPDATED':
      return t('K_REVIEW_ACTION_AUTHOR_FIELDS_UPDATED', 'Updated the details');
    case 'ANNOTATION_RESOLVED':
      return t('K_REVIEW_ACTION_ANNOTATION_RESOLVED', 'Resolved a comment');
    case 'DOCUMENT_COMMENT':
      return t('K_REVIEW_ACTION_DOCUMENT_COMMENT', 'Commented on the document');
    case 'DOCUMENT_SUGGESTION':
      // The headline is the sentinel's own count sentence; this is only the fallback for a
      // row whose message went missing.
      return t('K_REVIEW_ACTION_DOCUMENT_SUGGESTION', 'Suggested changes');
    case 'AUTHOR_AUTO_APPROVED':
      return t('K_REVIEW_ACTION_AUTHOR_AUTO_APPROVED', 'Approved automatically');
    case 'TOKEN_REGENERATED':
      return t('K_REVIEW_ACTION_TOKEN_REGENERATED', 'Reissued the review link');
    // BUG-7725 — the author side added or removed a reviewer; the message names them.
    case 'REVIEWER_ADDED':
      return t('K_REVIEW_ACTION_REVIEWER_ADDED', 'Added a reviewer');
    case 'REVIEWER_REMOVED':
      return t('K_REVIEW_ACTION_REVIEWER_REMOVED', 'Removed a reviewer');
    default:
      return t('K_REVIEW_ACTION_UNKNOWN', 'Updated the review');
  }
}

/* ── Whether it is waiting on you ───────────────────────────────────────────── */

/**
 * ⚠ There is deliberately NO "answering actions" allow-list.
 *
 * The obvious one — `CHANGES_DECIDED`, `CHANGES_RESPONDED`, `MARKED_COMPLETE` — cannot work,
 * because contract-service emits all three as `ActivityActorType.AUTHOR`
 * (`ReviewServiceImpl.java:767, 1117, 2030`). Both mount sites pass `viewer="REVIEWER"`, so a
 * viewer-scoped list of those never matches, the watermark stays at zero, and "Needs your
 * action" becomes a list that only ever grows — including items the reviewer answered in this
 * panel's own composer, since a reply is `NOTE_ADDED` and excluded as the viewer's own.
 *
 * What holds for BOTH sides is simply: the last time this side touched the review at all.
 */

/**
 * Epoch ms, or 0 for a value we cannot parse.
 *
 * NOT NaN: every comparator below subtracts two of these and falls through on a falsy result,
 * and `NaN` is falsy — so a single unparseable timestamp made the comparison intransitive and
 * `Array.sort`'s output implementation-defined for the whole list. 0 sorts such a row last in
 * a newest-first list, which is wrong but deterministic and local to that row.
 */
const at = (iso: string): number => {
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

export interface ActivityEntry {
  activity: ReviewActivity;
  reviewId: string;
  /** 1-based review cycle, for a target that has been round more than once. */
  cycle: number;
  kind: ActivityKind;
  /** Entries that reply to this one (BE V33). One level deep — a reply has no replies. */
  replies: ActivityEntry[];
  /**
   * A headline resolved by whoever built the entry, short-circuiting the action lookup.
   *
   * Set only on merged signing-audit events, whose `action` is an `AuditAction` and therefore
   * means nothing to {@link activityActionLabel}. When it is set the entry's `action` is
   * never read.
   */
  headline?: string;
}

/**
 * Flatten every review's activities, newest first, and mark the ones still waiting on
 * `viewer`.
 *
 * ⚠ There is NO "needs your action" flag on the wire — `ReviewActivity` carries only
 * `actorType`, `action`, `message` and `createdOn` — so this is derived, and deliberately
 * derived from the least that can go wrong: an entry is waiting on you when the OTHER side
 * asked for something and nothing from YOUR side has answered since. Anything cleverer would
 * be the frontend inventing review policy the backend does not have.
 */
export function buildActivityEntries(reviews: DocumentReview[]): ActivityEntry[] {
  const out: ActivityEntry[] = [];
  reviews.forEach((review, index) => {
    // `closed` and the `answeredAt` watermark stood here. They existed only to derive
    // `needsAction`, which went with the "Needs your action" section (2026-11-02), so they
    // are gone rather than left computing an answer nobody asks for.
    const sorted = [...review.activities]
      .filter((a) => !HIDDEN_ACTIONS.has(a.action))
      .sort((a, b) => at(a.createdOn) - at(b.createdOn));
    for (const activity of sorted) {
      out.push({
        activity,
        reviewId: review.id,
        cycle: index + 1,
        kind: activityKind(activity.action),
        replies: [],
      });
    }
  });
  /**
   * Nest replies under what they answer (BE V33's `parentActivityId`).
   *
   * One level only: a reply to a reply still points at the original, so a parent that is
   * itself a reply is treated as top level rather than nesting twice. A reply whose parent is
   * not in this list — an older row, or a parent in a cycle we are not showing — stays top
   * level rather than disappearing, which is the honest degradation.
   */
  const byId = new Map(out.map((e) => [e.activity.id, e]));
  const top: ActivityEntry[] = [];
  for (const entry of out) {
    const parentId = entry.activity.parentActivityId;
    const parent = parentId ? byId.get(parentId) : undefined;
    // Compared by ID rather than by object identity: if the server ever returned the same
    // activity id twice, `byId` keeps only the last one, and a self-parenting row would nest
    // under its own twin instead of being caught here.
    if (parent && parentId !== entry.activity.id && !parent.activity.parentActivityId) {
      parent.replies.push(entry);
    } else {
      top.push(entry);
    }
  }

  // Newest first, with the id as a tiebreak so two entries written in the same second keep a
  // stable order instead of depending on insertion. Replies read oldest-first beneath their
  // parent, because a thread is read forwards.
  const newestFirst = (a: ActivityEntry, b: ActivityEntry) =>
    at(b.activity.createdOn) - at(a.activity.createdOn) ||
    a.activity.id.localeCompare(b.activity.id);
  for (const entry of top) {
    entry.replies.sort((a, b) => -newestFirst(a, b));
  }
  return top.sort(newestFirst);
}

/** Does this signing-audit event reach the trail? The merge and the tab badge must agree. */
export function isTrailAuditEvent(event: AuditEvent): boolean {
  return !HIDDEN_AUDIT_ACTIONS.has(event.action);
}

/**
 * How many REVIEW entries the trail holds, flattened — replies included, hidden actions
 * excluded. A caller showing audit events too adds `events.filter(isTrailAuditEvent).length`;
 * both halves have to use the same predicate the panel renders by, or the badge drifts from
 * the list beneath it — which it has done twice now.
 *
 * `countNeedsAction` stood here and went with the "Needs your action" section it fed.
 */
export function countActivityEntries(reviews: DocumentReview[]): number {
  // No `viewer`: it only ever fed `needsAction`, which nothing reads any more. Keeping it
  // would invite a caller to pass one and believe the count were scoped to them.
  return buildActivityEntries(reviews).flatMap((e) => [e, ...e.replies]).length;
}

/* ── Where the review stands ────────────────────────────────────────────────── */

export type ReviewWaitingOn = 'reviewers' | 'workpay' | 'complete' | null;

/**
 * Who the review is sitting with, from the two booleans the DTO already carries. The design
 * calls this out at the top of the panel because it is the one thing a reviewer most often
 * opens the screen to find out.
 */
export function reviewWaitingOn(reviews: DocumentReview[]): ReviewWaitingOn {
  const active =
    reviews.find((r) => r.status === 'IN_PROGRESS' || r.status === 'PENDING') ?? reviews[0];
  if (!active) return null;
  if (active.authorCompleted && active.reviewerCompleted) return 'complete';
  if (active.reviewerCompleted) return 'workpay';
  return 'reviewers';
}

export function reviewWaitingLabel(state: ReviewWaitingOn, t: TFn): string | null {
  switch (state) {
    case 'reviewers':
      return t('K_REVIEW_WAITING_REVIEWERS', 'Waiting for the reviewers');
    case 'workpay':
      return t('K_REVIEW_WAITING_WORKPAY', 'Waiting for Workpay');
    case 'complete':
      return t('K_REVIEW_WAITING_COMPLETE', 'Review complete');
    default:
      return null;
  }
}

/* ── The envelope's workflow, woven into the review's history ───────────────── */

/**
 * Merge signing-audit events into a review's activity list, newest first.
 *
 * The prototype's rail is one history — "every document change, comment, response, review
 * action, workflow event and approval goes into it" (`ReviewerDocumentPage.jsx:17-19`). Ours
 * could only ever show the review half, because `review_activity.review_id` is NOT NULL: a
 * request that was sent, opened and signed recorded none of that here, and a request sent
 * WITHOUT a review had a permanently empty Activity tab with nothing saying why.
 *
 * A workflow event is deliberately inert in this list: never `needsAction` (nobody is being
 * asked for anything), never a reply parent, never a reply. It is something that happened to
 * the request.
 */
export function mergeWorkflowEvents(
  entries: ActivityEntry[],
  events: AuditEvent[] | undefined,
  t: TFn,
): ActivityEntry[] {
  if (!events || events.length === 0) return entries;

  const workflow: ActivityEntry[] = events.filter(isTrailAuditEvent).map((event) => ({
    activity: {
      // Prefixed so a signing-audit id can never collide with a review-activity id — both are
      // UUIDs from different tables, and these share one React key space.
      id: `audit-${event.id}`,
      reviewId: '',
      /**
       * Neither field below is read for a workflow entry. `actorType` is only consulted by
       * {@link buildActivityEntries}, which these bypass; `action` is only consulted by
       * `activityActionLabel`, which `headline` short-circuits. They are set to keep the
       * shared `ReviewActivity` shape rather than widening it for one case.
       */
      actorType: 'AUTHOR' as ReviewActorType,
      action: event.action as ReviewAction,
      actorName: event.actorName ?? event.actorEmail ?? '',
      actorEmail: event.actorEmail ?? '',
      createdOn: event.createdOn,
    },
    reviewId: '',
    // Workflow events belong to the request, not to a round of review, so they carry no cycle
    // of their own. 0 never equals a real cycle (which is 1-based), so the cycle heading
    // cannot be emitted for one.
    cycle: 0,
    kind: 'workflow' as ActivityKind,
    replies: [],
    headline: auditActionLabel(event.action, t),
  }));

  /**
   * Newest first, and DETERMINISTIC when two entries share a timestamp — which they routinely
   * do, because one action writes both a review row and an audit row in the same transaction.
   * The tiebreak used to be the id, i.e. arbitrary: `audit-…` against a bare uuid decided the
   * order of a step and the thing that caused it by string comparison.
   *
   * A workflow step is the CONSEQUENCE of the content beside it — the review finishes because
   * someone went through the changes — so at an equal stamp it sorts above, which in a
   * newest-first column means "and then this happened". The id remains the last resort so the
   * order never depends on input order.
   */
  const rank = (e: ActivityEntry) => (e.headline ? 0 : 1);
  return [...entries, ...workflow].sort(
    (a, b) =>
      at(b.activity.createdOn) - at(a.activity.createdOn) ||
      rank(a) - rank(b) ||
      a.activity.id.localeCompare(b.activity.id),
  );
}
